/**
 * GlobalCustomerSearch — unified customer search primitive.
 *
 * Port of legacy SPFx `PersonSearchBar` (spfx-account-opening-v2/.../people/
 * PersonSearchBar.tsx) + `CustSearchHelper` (spfx-account-opening-v2/.../services/
 * CustSearchHelper.ts), adapted to v3 api-client + shared-types + kill-list API.
 *
 * This is the single primitive that all 5 maintenance modules (RAM/RCM/RIM/RCD/
 * RSM), RAO People step, and RLQ should render — replacing their hand-rolled
 * search boxes. The audit (G-CC-03, G-XM-01, G-RAO-01..08) found each module
 * had re-invented search with slightly different (and mostly wrong) semantics.
 *
 * Behavior preserved verbatim from the legacy components:
 *   - Auto-detect SSN / CIF / single-name / first+last
 *   - Parallel CustSrch (up to 3) via Promise.all, dedupe by CifId
 *   - Error tolerance per-request (one failing search does not cancel the others)
 *   - Optional post-filter by account type (IRA=60, CD=30, SDB=50, ...) via
 *     CustAcctRelInq in parallel, annotated with acctCount + acctSummary
 *   - Alpha sort by lastName, firstName
 *   - Kill-list pre-filter from /v3/kill-list against pending-verification +
 *     verified entries, with graceful degradation if the fetch fails
 *   - Rich per-row card: initials avatar, custType pill, full SSN (not masked
 *     per feedback_ssn_masking.md), phone, truncated address, CIF right-aligned,
 *     "In Cart" badge + unclickable state when CifId is in cartCifIds
 *
 * Behaviors adapted for v3 (documented inline):
 *   - Uses `client.jxrl.post('Customer/CustSrch', ...)` with `queryParams` for the
 *     rich `PersonName` subfield + Count=N shape (the typed `accounts.custSrch`
 *     method only supports flat FirstName/LastName/TaxId/CifId and cannot
 *     express PersonName.FirstName + PersonName.LastName together).
 *   - Kill-list pre-filter calls `client.retail.killList.list()` twice (once per
 *     status) rather than the two SharePoint lists (ao_PipelineLogs +
 *     ao_KillList) the legacy checked — Bible §3.0 retires those SP lists.
 *   - Uses `unwrapJxrlArray` + `txt` + `hasJxrlError` from utils/jxrl.ts to
 *     handle all the jXRL response-shape quirks the legacy inlined.
 *   - Uses `formatSsn` from utils/ssn.ts (full SSN, never masked).
 */

import { useCallback, useMemo, useState } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { unwrapJxrlArray, txt, hasJxrlError } from '@/utils/jxrl';
import { formatSsn, restoreTaxIdLeadingZeros } from '@/utils/ssn';

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------

export interface CustomerSearchRow {
  cifId: string;
  name: string;
  firstName: string;
  lastName: string;
  /** Middle name as Core stores it (FULL, e.g. "Kent"), used to narrow a
   *  typed middle initial by prefix. Optional — Core omits it for many CIFs. */
  middleName?: string;
  /** FULL tax id — NEVER masked for internal Teams app per feedback_ssn_masking.md. */
  taxId: string;
  phone: string;
  address: string;
  /**
   * Date of birth (YYYY-MM-DD) when the CustSrch record carries `BirthDt`.
   * Core Director omits it on some CustSrch projections, so it's optional;
   * the no-dup-CIF name+DOB trigger (PeopleStep) treats a missing DOB as a
   * NAME-only candidate that still surfaces (the compare modal hydrates the
   * full on-file DOB for the FSR to confirm visually).
   */
  dob?: string;
  custType: 'Individual' | 'Business' | string;
  /** Populated when filterAcctType is supplied (IRA/CD/SDB flows). */
  acctType?: string;
  /** e.g. "2 IRAs" / "1 CD" — populated alongside acctType. */
  acctSummary?: string;
  /** Matching-account count when filterAcctType is supplied. */
  acctCount?: number;
}

export interface GlobalCustomerSearchProps {
  /**
   * Post-filter: keep only customers that have at least one account of this
   * type (e.g. "60"=IRA, "30"=CD, "50"=SDB). Legacy `filterAcctType` from
   * `CustSearchHelper.searchCustomers`.
   */
  filterAcctType?: string;
  /** Human-readable label for the filter (e.g. "IRA", "CD", "SDB"). */
  acctTypeLabel?: string;
  /** Callback fired when a result card is clicked. */
  onSelect: (row: CustomerSearchRow) => void;
  /** When provided, renders the "+ New Person" green button. */
  onAddNew?: () => void;
  /** CIFs already in the caller's cart — get "In Cart" badge + become unclickable. */
  cartCifIds?: ReadonlyArray<string>;
  /**
   * Scope for the remembered query (e.g. the cart id). The query survives
   * remounts WITHIN a scope but a new scope starts blank - Bill 2026-09-11:
   * "The Search entry needs to be cleared for each new cart."
   */
  persistScope?: string;
  /** Max results to request per parallel CustSrch call (default 25). */
  maxResults?: number;
  /** Placeholder override for the query input. */
  placeholder?: string;
  /** Compact card layout (shorter rows, less whitespace) for sidebars/modals. */
  compact?: boolean;
}

// -----------------------------------------------------------------------------
// Internal helpers (port of legacy splitFullName + formatting utilities)
// -----------------------------------------------------------------------------

function splitFullName(q: string): { firstName: string; lastName: string; middleName: string } {
  const parts = q.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '', middleName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '', middleName: '' };
  if (parts.length === 2) return { firstName: parts[0], lastName: parts[1], middleName: '' };
  // 3+ tokens: FIRST is the first token, LAST is the LAST token, everything
  // between is the middle name. The old code did lastName = parts.slice(1),
  // so "John K Wiggins" searched LastName="K Wiggins" and matched nobody —
  // adding a middle initial made a search FAIL that worked without it
  // (Bill + Collette, 2026-07-27).
  //
  // The middle name is deliberately NOT sent to Core. Core stores the FULL
  // middle name ("Kent"), so querying MiddleName="K" would miss the very
  // person being searched for. It is used below to rank/narrow the results
  // client-side by PREFIX, which matches K -> Kent and E -> E alike.
  return {
    firstName: parts[0],
    lastName: parts[parts.length - 1],
    middleName: parts.slice(1, -1).join(' '),
  };
}

/**
 * Narrow results by a typed middle name/initial WITHOUT hiding real matches.
 * Prefix-matches against the row's middle name; if nothing matches (e.g. Core
 * has no middle name recorded) the full set is returned unchanged, so typing a
 * middle initial can never turn a working search into "no results".
 */
function narrowByMiddleName<T extends { middleName?: string }>(rows: T[], middleName: string): T[] {
  const m = middleName.trim().toLowerCase().replace(/\.$/, '');
  if (!m) return rows;
  const hits = rows.filter((r) => (r.middleName ?? '').trim().toLowerCase().startsWith(m));
  return hits.length > 0 ? hits : rows;
}

function formatPhone(raw: string): string {
  if (!raw) return '';
  return raw.length === 10 ? `${raw.slice(0, 3)}-${raw.slice(3, 6)}-${raw.slice(6)}` : raw;
}

/**
 * Parse one jXRL CustSrch response into CustomerSearchRow[]. Dedupe happens at
 * the caller level across multiple responses.
 */
function parseCustSrchResponse(resp: unknown): CustomerSearchRow[] {
  if (hasJxrlError(resp)) return [];
  const rows = unwrapJxrlArray<Record<string, unknown>>(resp, 'CustSrchRecArray', 'CustSrchRec');
  const out: CustomerSearchRow[] = [];
  for (const c of rows) {
    // CustId arrives EITHER as a plain string OR as the masked-attribute
    // shape {"#text":"...","@MaskVal":"..."} — the same shape TaxId uses
    // right below. String() on the object form yields the literal
    // "[object Object]", which was ONE root cause behind THREE symptoms
    // (Bill + Collette, 2026-07-27): every row got the same cifId, so the
    // dedupe collapsed all matches to a single result; the tile rendered
    // "CIF [object Object]"; and the in-cart index keyed on that literal,
    // so once ANY person was in the cart EVERY search result showed
    // "In Cart". Use txt() exactly as TaxId does.
    const cifId = txt(c.CustId);
    if (!cifId) continue;
    const pn = (c.PersonName as Record<string, unknown> | undefined) ?? {};
    const firstName = typeof pn.FirstName === 'string' ? pn.FirstName : '';
    const lastName = typeof pn.LastName === 'string' ? pn.LastName : '';
    const comName = typeof pn.ComName === 'string' ? pn.ComName : '';
    const middleName = typeof pn.MiddleName === 'string' ? pn.MiddleName : '';

    // TaxId may be plain string OR {"#text":"...","@MaskVal":"XXX-XX-..."} —
    // `txt()` returns the raw #text (or the string itself).
    // Core returns TaxId as a JSON number for some CIFs, which cannot carry a
    // leading zero; restore it so LexisNexis gets a complete 9-digit id.
    const taxId = restoreTaxIdLeadingZeros(txt(c.TaxId));

    const addrObj = (c.Addr as Record<string, unknown> | undefined) ?? {};
    const street = typeof addrObj.StreetAddr1 === 'string' ? addrObj.StreetAddr1 : '';
    const city = typeof addrObj.City === 'string' ? addrObj.City : '';
    const stCode = typeof addrObj.StateCode === 'string' ? addrObj.StateCode : '';
    const address = street ? `${street}, ${city} ${stCode}`.trim() : '';

    const acctObj = (c.AccountId as Record<string, unknown> | undefined) ?? {};
    const acctType = typeof acctObj.AcctType === 'string' ? acctObj.AcctType : undefined;

    const custTypeCode = typeof c.CustType === 'string' ? c.CustType : '';
    const custType: CustomerSearchRow['custType'] =
      custTypeCode === 'I' ? 'Individual' : custTypeCode === 'C' ? 'Business' : custTypeCode;

    out.push({
      cifId,
      middleName,
      name: comName || `${firstName} ${lastName}`.trim(),
      firstName,
      lastName,
      taxId,
      phone: typeof c.PhoneNum === 'string' ? c.PhoneNum : '',
      address,
      custType,
      ...(acctType ? { acctType } : {}),
    });
  }
  return out;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

/**
 * Primitive: GlobalCustomerSearch.
 *
 * Ported from legacy PersonSearchBar; wire this in place of per-module search
 * inputs in RAM / RCM / RIM / RCD / RSM / RAO People / RLQ (Wave 2).
 */
export function GlobalCustomerSearch({
  filterAcctType,
  acctTypeLabel,
  onSelect,
  onAddNew,
  cartCifIds,
  persistScope,
  maxResults = 25,
  placeholder = '000-00-0000, Smith, John Smith, or CIF #',
  compact = false,
}: GlobalCustomerSearchProps): JSX.Element {
  const client = useApiClient();
  // Bill 2026-05-11 — persist query across remounts. The cart-add flow
  // unmounts this component (loading overlay swap, dialog open/close) and
  // useState('') resets. sessionStorage survives the remount so the FSR
  // keeps their search context across consecutive person picks.
  const SEARCH_PERSIST_KEY = persistScope ? `rop:gcs:query:${persistScope}` : 'rop:gcs:query';
  const [query, setQuery] = useState<string>(() => {
    try { return sessionStorage.getItem(SEARCH_PERSIST_KEY) ?? ''; }
    catch { return ''; }
  });
  // Mirror writes to sessionStorage on every change.
  const setQueryPersistent = (next: string): void => {
    setQuery(next);
    try { sessionStorage.setItem(SEARCH_PERSIST_KEY, next); } catch { /* ignore */ }
  };
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<CustomerSearchRow[]>([]);
  const [searchError, setSearchError] = useState('');
  const [selectingCif, setSelectingCif] = useState('');

  const cartIndex = useMemo<Record<string, true>>(() => {
    const idx: Record<string, true> = {};
    for (const id of cartCifIds ?? []) {
      if (id) idx[id] = true;
    }
    return idx;
  }, [cartCifIds]);

  // ---------------------------------------------------------------------------
  // Primary search handler (auto-detect SSN / CIF / name)
  // ---------------------------------------------------------------------------

  const handleSearch = useCallback(async (): Promise<void> => {
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    setSearchError('');
    setResults([]);

    const countQs = `Count=${maxResults}`;

    // Track whether any CustSrch request ACTUALLY succeeded end-to-end.
    // A single failure must not cancel the others (legacy swallowed errors via
    // .catch), but we need to distinguish "jXchange/Core Director down" from
    // "real empty result" — otherwise the user sees "No customers found" when
    // the issue is actually a transport failure and they're misled into
    // re-searching repeatedly. See Bill's 2026-04-22 feedback.
    let successCount = 0;
    let failureCount = 0;

    try {
      const isSsnLike = /^\d{3}-?\d{2}-?\d{4}$/.test(q) || /^\d{9}$/.test(q);
      const isCifLike = /^\d{6,}$/.test(q);

      // Build up to 3 parallel CustSrch bodies — mirrors legacy PersonSearchBar
      // lines 78-93.
      const requests: Array<Promise<unknown>> = [];
      const post = (body: Record<string, unknown>): Promise<unknown> =>
        client.jxrl
          .post('Customer/CustSrch', body, { queryParams: countQs })
          .then((r) => {
            successCount++;
            return r;
          })
          .catch(() => {
            failureCount++;
            // Sentinel preserves legacy resilience — other requests still merge.
            return { Details: [{ Code: 'ERR' }] };
          });

      if (isSsnLike) {
        requests.push(post({ TaxId: q.replace(/\D/g, '') }));
      } else {
        const { firstName, lastName } = splitFullName(q);
        if (firstName && lastName) {
          requests.push(post({ PersonName: { FirstName: firstName, LastName: lastName } }));
        } else if (firstName) {
          // Single-word name — try last-name AND first-name in parallel + dedupe
          requests.push(post({ PersonName: { LastName: firstName } }));
          requests.push(post({ PersonName: { FirstName: firstName } }));
        }
        if (isCifLike) {
          requests.push(post({ CustId: q }));
        }
      }

      const settled = await Promise.all(requests);

      // Dedupe by CifId across all three response shapes.
      const seen = new Set<string>();
      let merged: CustomerSearchRow[] = [];
      for (const resp of settled) {
        for (const row of parseCustSrchResponse(resp)) {
          if (seen.has(row.cifId)) continue;
          seen.add(row.cifId);
          merged.push(row);
        }
      }

      // Alpha sort by lastName, then firstName (fall back to name).
      merged.sort((a, b) => {
        const lastCmp = (a.lastName || a.name).localeCompare(b.lastName || b.name);
        if (lastCmp !== 0) return lastCmp;
        return (a.firstName || '').localeCompare(b.firstName || '');
      });

      // -----------------------------------------------------------------------
      // Kill-list pre-filter (v3 port of legacy ao_PipelineLogs + ao_KillList
      // SharePoint queries at PersonSearchBar.tsx:140-188).
      // -----------------------------------------------------------------------
      if (merged.length > 0) {
        const killedCifs = new Set<string>();
        try {
          const [pending, verified] = await Promise.all([
            client.retail.killList
              .list({ status: 'pending-verification', entryType: 'CifKill', top: 500 })
              .catch(() => null),
            client.retail.killList
              .list({ status: 'verified', entryType: 'CifKill', top: 500 })
              .catch(() => null),
          ]);
          for (const page of [pending, verified]) {
            if (!page?.entries) continue;
            for (const entry of page.entries) {
              // Test-application cleanup (reason TestAccount) is not a real kill: the
              // test customer stays reusable on further test applications until its
              // cleanup runs, and the server holds that kill while another open
              // application uses the customer (Bill 2026-10-06).
              if (entry.entryType === 'CifKill' && entry.targetId && entry.reason !== 'TestAccount') {
                killedCifs.add(entry.targetId);
              }
            }
          }
        } catch {
          // Kill-list fetch failed — legacy logs + shows unfiltered results.
          // We preserve that behavior.
          // eslint-disable-next-line no-console
          console.warn('[GlobalCustomerSearch] kill-list fetch failed — showing unfiltered');
        }

        if (killedCifs.size > 0) {
          const filtered = merged.filter((m) => !killedCifs.has(m.cifId));
          if (filtered.length === 0) {
            // All results were killed — surface the explicit kill-list message
            // rather than a generic "no results" banner (legacy line 171).
            setResults([]);
            setSearchError('All matching records are on the kill list');
            setSearching(false);
            return;
          }
          merged = filtered;
        }
      }

      // -----------------------------------------------------------------------
      // Optional post-filter by account type (IRA=60, CD=30, SDB=50, ...) —
      // legacy CustSearchHelper.searchCustomers lines 80-108.
      // -----------------------------------------------------------------------
      if (filterAcctType && merged.length > 0) {
        const annotated: CustomerSearchRow[] = [];
        await Promise.all(
          merged.map(async (row) => {
            try {
              const relResp = (await client.jxrl.post(
                'Customer/CustAcctRelInq',
                { CustId: row.cifId },
                { queryParams: 'Count=50' }
              )) as Record<string, unknown>;
              if (hasJxrlError(relResp)) return;
              const rels = unwrapJxrlArray<Record<string, unknown>>(
                relResp,
                'CustAcctRelInqRecArray',
                'CustAcctRelInqRec'
              );
              let match = 0;
              for (const rel of rels) {
                const acctObj =
                  (rel.AcctId as Record<string, unknown> | undefined) ??
                  (rel.AccountId as Record<string, unknown> | undefined) ??
                  {};
                if (typeof acctObj.AcctType === 'string' && acctObj.AcctType === filterAcctType) {
                  match++;
                }
              }
              if (match > 0) {
                const label = acctTypeLabel ?? 'acct';
                annotated.push({
                  ...row,
                  acctCount: match,
                  acctSummary: `${match} ${label}${match > 1 ? 's' : ''}`,
                });
              }
            } catch {
              // Skip failed lookups — legacy `.catch(function() { /* skip */ })`.
            }
          })
        );
        merged = annotated;
      }

      if (merged.length === 0) {
        // Distinguish transport failure from genuine empty result.
        // If every CustSrch request hit the catch branch (0 successes, >=1
        // failures) we're almost certainly facing the intermittent jXchange /
        // Core Director outage — show the actionable connection-issue message
        // with a wait-and-retry instruction instead of the misleading
        // "No customers found" banner.
        if (successCount === 0 && failureCount > 0) {
          setSearchError(
            'JHA Core Director jXchange connection issue detected. Please wait 10 seconds, and try again. You may need to reload the app if the issue persists.'
          );
        } else {
          setSearchError('No matching customers found.');
        }
      }
      setResults(merged);
    } catch (err) {
      // Reaching the outer catch means something outside the per-request
      // catch swallowed — almost always a transport-level failure. Surface
      // the same actionable message.
      const msg = err instanceof Error ? err.message : 'Search failed';
      if (/network|fetch|timeout|TypeError|abort/i.test(msg)) {
        setSearchError(
          'JHA Core Director jXchange connection issue detected. Please wait 10 seconds, and try again. You may need to reload the app if the issue persists.'
        );
      } else {
        setSearchError(msg);
      }
    } finally {
      setSearching(false);
    }
  }, [client, query, maxResults, filterAcctType, acctTypeLabel]);

  // ---------------------------------------------------------------------------
  // Selection — caller owns what to do with the row. We only mark the card as
  // "loading" briefly for visual feedback (parity with legacy `loadingCif`).
  // ---------------------------------------------------------------------------
  const handleClick = useCallback(
    (row: CustomerSearchRow): void => {
      if (cartIndex[row.cifId]) return; // unclickable when already in cart
      setSelectingCif(row.cifId);
      try {
        onSelect(row);
      } finally {
        // Clear on the next tick so callers that do async work still see the
        // highlight while they fetch CustInq.
        setTimeout(() => setSelectingCif((cur) => (cur === row.cifId ? '' : cur)), 0);
      }
    },
    [cartIndex, onSelect]
  );

  // ---------------------------------------------------------------------------
  // Render — PCB brand tokens only. No CDN fonts.
  // ---------------------------------------------------------------------------
  const FONT_STACK = "'Roboto', system-ui, -apple-system, 'Segoe UI', sans-serif";
  // `compact` prop reserved for callers that embed this primitive in dense
  // contexts (dropdowns, side panels). Card padding + gap now live in the
  // card-grid class, so the compact flag is effectively a density hint the
  // card styles will read in a future pass.
  void compact;

  return (
    <div
      className="gcs" data-tour="cart-person-search"
      style={{
        background: 'var(--surface-card)',
        border: '1px solid var(--surface-border)',
        borderRadius: '8px',
        padding: compact ? '12px' : '16px',
        fontFamily: FONT_STACK,
      }}
    >
      <div className="gcs__row" style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: '12px', color: 'var(--ink-600)', display: 'block', marginBottom: '4px' }}>
            Search by SSN, name, or CIF number
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={query}
              onChange={(e) => setQueryPersistent(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleSearch();
              }}
              placeholder={placeholder}
              style={{
                width: '100%',
                padding: '10px 12px',
                paddingRight: 32,
                borderRadius: '6px',
                border: '1px solid var(--surface-border)',
                // iOS auto-zooms any focused <input> with font-size < 16px.
                // Bill 2026-05-11 — keep at 16px so the viewport never zooms
                // on mobile Teams.
                fontSize: '16px',
                fontFamily: FONT_STACK,
                boxSizing: 'border-box',
              }}
            />
            <button
              type="button"
              onClick={() => void handleSearch()}
              title="Search"
              aria-label="Search"
              style={{
                position: 'absolute',
                right: 4,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--ink-600)',
                fontSize: '16px',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {'\u{1F50D}'}
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={() => void handleSearch()}
          disabled={searching || !query.trim()}
          style={{
            padding: '10px 24px',
            background: 'var(--brand-fill)',
            color: 'var(--on-brand)',
            border: 'none',
            borderRadius: '4px',
            fontSize: '14px',
            fontFamily: FONT_STACK,
            cursor: searching || !query.trim() ? 'default' : 'pointer',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            opacity: searching || !query.trim() ? 0.6 : 1,
          }}
        >
          {searching ? 'Searching…' : 'Search'}
        </button>
        {onAddNew && (
          <button
            type="button"
            onClick={onAddNew} data-tour="cart-new-person"
            style={{
              padding: '10px 24px',
              background: 'var(--ok-fill)',
              color: 'var(--on-brand)',
              border: 'none',
              borderRadius: '4px',
              fontSize: '14px',
              fontFamily: FONT_STACK,
              cursor: 'pointer',
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
          >
            + New Person
          </button>
        )}
      </div>

      {searchError && (
        <div
          role="alert"
          style={{
            marginTop: '8px',
            padding: '8px 12px',
            background: 'var(--err-bg)',
            color: 'var(--pcb-red)',
            borderRadius: '4px',
            fontSize: '13px',
          }}
        >
          {searchError}
        </div>
      )}

      {results.length > 0 && (
        <div style={{ marginTop: '12px', borderTop: '1px solid var(--surface-border)', paddingTop: '12px' }}>
          <div style={{ fontSize: '12px', color: 'var(--ink-600)', marginBottom: '8px' }}>
            {results.length} result{results.length !== 1 ? 's' : ''} found
          </div>
          {/* App-wide pattern (Bill 2026-04-22): list surfaces use .rop-card-grid */}
          {/* auto-fit columns instead of stacking one-card-per-row. See globals.css */}
          {/* for variants (--sm, --lg).                                              */}
          <div className="rop-card-grid">
          {results.map((r) => {
            const alreadyInCart = !!cartIndex[r.cifId];
            const isLoadingThis = selectingCif === r.cifId;
            const isBusiness = r.custType === 'Business';
            return (
              <div
                key={r.cifId}
                role={alreadyInCart ? undefined : 'button'}
                tabIndex={alreadyInCart ? -1 : 0}
                onClick={() => handleClick(r)}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && !alreadyInCart) {
                    e.preventDefault();
                    handleClick(r);
                  }
                }}
                style={{
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: `1px solid ${alreadyInCart ? 'var(--ok-text)' : 'var(--surface-border)'}`,
                  cursor: alreadyInCart ? 'default' : 'pointer',
                  background: alreadyInCart ? 'var(--ok-bg)' : isLoadingThis ? 'var(--info-bg)' : 'var(--surface-card)',
                  opacity: alreadyInCart ? 0.7 : 1,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
                  minWidth: 0,
                }}
                onMouseEnter={(e) => {
                  if (!alreadyInCart) {
                    e.currentTarget.style.borderColor = 'var(--pcb-blue)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(41, 104, 173, 0.12)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!alreadyInCart) {
                    e.currentTarget.style.borderColor = 'var(--surface-border)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }
                }}
              >
                {/* Row 1: avatar + name + type pill + CIF/In-Cart badge */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div
                      aria-hidden
                      style={{
                        width: '34px',
                        height: '34px',
                        borderRadius: '50%',
                        background: isBusiness ? 'var(--brand-fill-strong)' : 'var(--brand-fill)',
                        color: 'var(--on-brand)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '13px',
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      {(r.firstName?.charAt(0) ?? '').toUpperCase() || '?'}
                      {(r.lastName?.charAt(0) ?? '').toUpperCase()}
                    </div>
                    <div>
                      <span style={{ fontSize: '15px', fontWeight: 500, color: 'var(--ink-700)' }}>{r.name}</span>
                      <span
                        style={{
                          marginLeft: '8px',
                          padding: '1px 8px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: 600,
                          background: isBusiness ? 'var(--brand-fill-strong)' : 'var(--info-bg)',
                          color: isBusiness ? 'var(--on-brand)' : 'var(--pcb-blue)',
                        }}
                      >
                        {r.custType || 'Customer'}
                      </span>
                      {r.acctSummary && (
                        <span
                          style={{
                            marginLeft: '6px',
                            padding: '1px 8px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: 600,
                            background: 'var(--info-bg)',
                            color: 'var(--pcb-blue-dark)',
                          }}
                        >
                          {r.acctSummary}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {alreadyInCart && (
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: 600,
                          background: 'var(--ok-bg)',
                          color: 'var(--pcb-green)',
                        }}
                      >
                        In Cart
                      </span>
                    )}
                    {isLoadingThis ? (
                      <span style={{ fontSize: '12px', color: 'var(--pcb-blue)', fontWeight: 500 }}>Loading…</span>
                    ) : (
                      <span style={{ fontSize: '12px', color: 'var(--ink-600)' }}>CIF {r.cifId}</span>
                    )}
                  </div>
                </div>
                {/* Row 2: SSN + phone + address. No left indent -- the card   */}
                {/* is narrow enough (~320-380px) in the auto-fit grid that     */}
                {/* hanging the metadata under the avatar would waste ~40% of    */}
                {/* the card width. Flow under full width with flex-wrap.        */}
                <div
                  style={{
                    display: 'flex',
                    gap: '8px 14px',
                    marginTop: '6px',
                    fontSize: '11px',
                    color: 'var(--ink-600)',
                    flexWrap: 'wrap',
                    minWidth: 0,
                  }}
                >
                  {r.taxId && (
                    <span title="SSN/TIN" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatSsn(r.taxId)}
                    </span>
                  )}
                  {r.phone && (
                    <span title="Phone" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatPhone(r.phone)}
                    </span>
                  )}
                  {r.address && (
                    <span
                      title={r.address}
                      style={{
                        flex: '1 1 auto',
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.address}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          </div>
        </div>
      )}
    </div>
  );
}
